/**
 * QuickNavEditor.js
 *
 * Dev-only authoring panel for the QuickNav footer + Guided Tour stop list.
 * Both surfaces share a single source of truth: `src/config/quickNavConfig.json`.
 *
 * Per-stop the curator chooses:
 *   • Target POI       — picked from the POI list authored in NavHintsEditor
 *                        (`public/config/navHintsConfig.json`).
 *   • Audio narration  — narration key family (3 langs × connected/standalone).
 *   • Pose             — captured sweepId+yaw used as the precise camera frame.
 *   • Auto-pan         — toggles the cinematic left→right pan on arrival.
 *
 * Add / remove / reorder rows is supported. Saves to
 * `src/config/quickNavConfig.json` via the dev-server middleware in
 * `setupProxy.js` (only available when running `npm start`).
 *
 * Gated by Alt+Shift+E (master edit mode).
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import matterportService from '../services/matterportService';
import quickNavConfigSrc from '../config/quickNavConfig.json';
import { Z_INDEX } from '../const';
import { useEditMode } from '../utils/editMode';
import { NARRATION_KEYS } from '../utils/editorAssets';

// ── Styling tokens ────────────────────────────────────────────────────
const panelStyle = {
  position: 'fixed',
  top: 70,
  right: 16,
  width: 380,
  maxHeight: 'calc(100vh - 120px)',
  overflowY: 'auto',
  zIndex: Z_INDEX.QUICK_NAV_EDITOR,
  background: 'rgba(18,18,22,0.96)',
  color: '#f5f5f5',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 10,
  padding: 14,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: 13,
  boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
};
const row = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 };
const label = { minWidth: 90, color: '#bbb', fontSize: 12 };
const input = {
  flex: 1,
  background: '#1f1f26',
  color: '#f5f5f5',
  border: '1px solid #3a3a44',
  borderRadius: 4,
  padding: '4px 6px',
  fontSize: 12,
};
const btn = {
  background: '#2d6cdf',
  color: '#fff',
  border: 0,
  borderRadius: 4,
  padding: '6px 10px',
  cursor: 'pointer',
  fontSize: 12,
};
const btnSecondary = { ...btn, background: '#3a3a44' };
const btnDanger = { ...btn, background: '#a13c2e' };
const btnGhost = { ...btnSecondary, padding: '2px 6px', fontSize: 11 };

const clone = (o) => JSON.parse(JSON.stringify(o));

// ── Helpers ───────────────────────────────────────────────────────────

// Map the legacy schema to the simplified shape used by the editor UI.
const normaliseLocation = (loc) => {
  const cam = loc.camera || {};
  // pan toggle: explicit `pan` field wins; legacy panDegrees>0 → on.
  let pan;
  if (typeof cam.pan === 'boolean') pan = cam.pan;
  else if (typeof cam.panDegrees === 'number') pan = cam.panDegrees > 0;
  else pan = false;
  // Per-stop pan angle (override the default ±15°). Falls back to legacy
  // panDegrees field when present so existing configs keep working.
  let panAngle = null;
  if (typeof cam.panAngle === 'number') panAngle = cam.panAngle;
  else if (typeof cam.panDegrees === 'number' && cam.panDegrees > 0) panAngle = cam.panDegrees;
  // Per-stop pan speed (default 1). Lower = slower / more cinematic.
  const panSpeed = (typeof cam.panSpeed === 'number' && cam.panSpeed > 0) ? cam.panSpeed : null;
  return {
    ...loc,
    _pan: pan,
    _panAngle: panAngle,
    _panSpeed: panSpeed,
  };
};

// Build the on-disk shape from the editor's working copy. Strips the
// `_*` derived fields and removes empty/no-op entries so the JSON
// stays clean.
const serialise = (working) => {
  const out = clone(working);
  for (const loc of out.locations) {
    const pan = !!loc._pan;
    const panAngle = (typeof loc._panAngle === 'number' && loc._panAngle > 0) ? loc._panAngle : null;
    const panSpeed = (typeof loc._panSpeed === 'number' && loc._panSpeed > 0) ? loc._panSpeed : null;
    delete loc._pan;
    delete loc._panAngle;
    delete loc._panSpeed;
    // Camera block: keep sweepId + rotation (captured pose). Use
    // `pan` boolean + optional `panAngle` / `panSpeed` overrides.
    if (loc.camera) {
      loc.camera = {
        ...loc.camera,
        pan,
      };
      if (panAngle != null) loc.camera.panAngle = panAngle;
      else delete loc.camera.panAngle;
      if (panSpeed != null) loc.camera.panSpeed = panSpeed;
      else delete loc.camera.panSpeed;
      delete loc.camera.panDegrees;
      delete loc.camera.skipCamera;
      delete loc.camera.flyTime;
      // Drop empty camera blocks.
      if (!loc.camera.sweepId && !loc.camera.rotation) {
        delete loc.camera;
      }
    }
  }
  return out;
};

const QuickNavEditor = ({ active = true }) => {
  const editMode = useEditMode();
  const [open, setOpen] = useState(false);
  const [working, setWorking] = useState(() => {
    const c = clone(quickNavConfigSrc);
    c.locations = (c.locations || []).map(normaliseLocation);
    return c;
  });
  const [selId, setSelId] = useState(() => quickNavConfigSrc.locations?.[0]?.id || '');
  const [pois, setPois] = useState([]); // [{ id, label, position }]
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  // Pan-angle slider live preview: captures the camera's base rotation
  // on slider grab, drives setRotation to (base.y + angle) on each input
  // change, and snaps back to base on release.
  const panPreviewBaseRef = useRef(null);

  const selected = useMemo(
    () => working.locations.find((l) => l.id === selId) || null,
    [working, selId]
  );

  // Push live overrides into the service whenever the working copy changes
  // so authoring is reflected in nav without saving.
  useEffect(() => {
    const map = {};
    for (const loc of working.locations) {
      if (loc.tourSid && loc.camera) {
        const angle = (typeof loc._panAngle === 'number' && loc._panAngle > 0) ? loc._panAngle : 15;
        const speed = (typeof loc._panSpeed === 'number' && loc._panSpeed > 0) ? loc._panSpeed : 1;
        // IMPORTANT: spread loc.camera FIRST then overwrite the pan-related
        // fields with the editor's working values. Otherwise the saved
        // `pan` / `panAngle` from disk wins because runtime checks them
        // before `panDegrees`, and live edits never take effect.
        map[loc.tourSid] = {
          ...loc.camera,
          pan: !!loc._pan,
          panAngle: loc._pan ? angle : undefined,
          panDegrees: loc._pan ? angle : 0,
          panSpeed: loc._pan ? speed : 0,
        };
      }
    }
    matterportService.setLocationCameraOverrides?.(map);
  }, [working]);

  // Load POI list from navHintsConfig.json (POIs authored by the user).
  // Re-runs each time the editor opens so newly-added POIs appear without
  // a full page reload. Cache-bust is required because the dev server
  // serves the static JSON with caching headers.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetch(`/config/navHintsConfig.json?t=${Date.now()}`, { cache: 'no-store' })
      .then((r) => r.json())
      .then((cfg) => {
        if (cancelled) return;
        const hints = Array.isArray(cfg) ? cfg : (cfg.hints || []);
        const list = hints
          .filter((h) => h && h.type === 'poi' && h.position)
          .map((h) => ({
            id: h.id,
            label:
              h.labels?.en ||
              h.labels?.mr ||
              h.labels?.hi ||
              h.id,
            position: h.position,
          }));
        setPois(list);
      })
      .catch(() => setPois([]));
    return () => { cancelled = true; };
  }, [open]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2400);
  }, []);

  const updateLocation = useCallback((id, mutator) => {
    setWorking((prev) => {
      const next = clone(prev);
      const loc = next.locations.find((l) => l.id === id);
      if (loc) mutator(loc);
      return next;
    });
  }, []);

  // ── Actions ─────────────────────────────────────────────────────────

  const handleFlyTo = useCallback(async (id) => {
    const loc = working.locations.find((l) => l.id === id);
    if (!loc) return;
    setSelId(id);
    await matterportService.stopTour?.({ skipReturn: true });
    if (loc.tourSid) {
      await matterportService.navigateToTourSnapshotBySid(loc.tourSid, loc.camera || null);
    } else {
      showToast('No tourSid for this stop');
    }
  }, [working, showToast]);

  // Capture the live SDK camera pose (sweep + rotation + zoom) and store
  // it as the stop's default view. The author drives the 3D scene to the
  // desired angle and zoom level, then clicks to lock it in.
  const handleCapture = useCallback(async (id) => {
    const sweepId = matterportService.getCurrentSweepId?.() || '';
    const pose = matterportService.getCurrentPose?.() || null;
    const zoom = matterportService.getCurrentZoom?.();
    if (!sweepId && !pose?.rotation) {
      showToast('SDK not ready — move around first');
      return;
    }
    updateLocation(id, (l) => {
      const prev = l.camera || {};
      l.camera = {
        ...prev,
        sweepId: sweepId || prev.sweepId || '',
        rotation: pose?.rotation
          ? { x: Number((pose.rotation.x ?? 0).toFixed(2)),
              y: Number((pose.rotation.y ?? 0).toFixed(2)) }
          : prev.rotation || { x: 0, y: 0 },
        ...(typeof zoom === 'number'
          ? { zoom: Number(zoom.toFixed(3)) }
          : {}),
      };
    });
    showToast('Default view captured (position + angle + zoom)');
  }, [updateLocation, showToast]);

  const handleClearCamera = useCallback((id) => {
    updateLocation(id, (l) => { delete l.camera; });
    showToast('Pose cleared');
  }, [updateLocation, showToast]);

  const handleMove = useCallback((id, delta) => {
    setWorking((prev) => {
      const next = clone(prev);
      const i = next.locations.findIndex((l) => l.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= next.locations.length) return prev;
      const [item] = next.locations.splice(i, 1);
      next.locations.splice(j, 0, item);
      return next;
    });
  }, []);

  const handleDelete = useCallback((id) => {
    if (!window.confirm(`Remove stop "${id}"? This affects both Quick Nav and the Guided Tour.`)) return;
    setWorking((prev) => {
      const next = clone(prev);
      next.locations = next.locations.filter((l) => l.id !== id);
      return next;
    });
    if (selId === id) setSelId(working.locations[0]?.id || '');
    showToast('Stop removed');
  }, [selId, working, showToast]);

  const handleAdd = useCallback(() => {
    const id = `stop_${Date.now().toString(36)}`;
    setWorking((prev) => {
      const next = clone(prev);
      next.locations.push({
        id,
        labels: { mr: 'नवीन स्थान', en: 'New Stop', hi: 'नया स्थान' },
        tourSid: '',
        narrationKey: '',
        targetPoiId: '',
        _pan: false,
      });
      return next;
    });
    setSelId(id);
    showToast('Stop added — fill in target POI & audio');
  }, [showToast]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload = serialise(working);
      const res = await fetch('/__dev/saveQuickNav', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        showToast(`Save failed: ${json.error || res.status}`);
      } else {
        showToast(`Saved ${json.count} stops → quickNavConfig.json`);
      }
    } catch (err) {
      showToast(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }, [working, showToast]);

  const handleCopyJson = useCallback(() => {
    const payload = serialise(working);
    navigator.clipboard?.writeText(JSON.stringify(payload, null, 2)).then(
      () => showToast('JSON copied'),
      () => showToast('Copy failed'),
    );
  }, [working, showToast]);

  // ── Render ──────────────────────────────────────────────────────────

  if (!active || !editMode) return null;

  if (!open) {
    return (
      <button
        style={{
          ...btn,
          position: 'fixed',
          top: 70,
          right: 16,
          zIndex: Z_INDEX.QUICK_NAV_EDITOR,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        }}
        onClick={() => setOpen(true)}
      >
        🎥 Tour & QuickNav Edit
      </button>
    );
  }

  const setSel = (mutator) => {
    if (!selected) return;
    updateLocation(selected.id, mutator);
  };

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>🎥 Tour & QuickNav Editor</strong>
        <button style={btnSecondary} onClick={() => setOpen(false)}>×</button>
      </div>

      {/* Stop list with reorder/delete */}
      <div style={{ border: '1px solid #2a2a32', borderRadius: 6, marginBottom: 10, maxHeight: 260, overflowY: 'auto' }}>
        {working.locations.map((l, i) => {
          const isSel = l.id === selId;
          const hasPose = !!l.camera?.sweepId;
          const dot = hasPose ? '#4ade80' : '#555';
          return (
            <div
              key={l.id}
              onClick={() => setSelId(l.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '5px 6px',
                background: isSel ? '#2d4a7a' : 'transparent',
                borderBottom: '1px solid #242430',
                cursor: 'pointer',
              }}
            >
              <span style={{ width: 18, color: '#666', fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>{i + 1}</span>
              <span style={{ color: dot, marginRight: 2 }}>●</span>
              <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.labels?.en || l.id}
              </span>
              <button style={btnGhost} title="Move up" onClick={(e) => { e.stopPropagation(); handleMove(l.id, -1); }}>↑</button>
              <button style={btnGhost} title="Move down" onClick={(e) => { e.stopPropagation(); handleMove(l.id, +1); }}>↓</button>
              <button style={btnGhost} title="Fly here" onClick={(e) => { e.stopPropagation(); handleFlyTo(l.id); }}>✈️</button>
              <button style={{ ...btnGhost, background: '#5e2a2a' }} title="Remove stop" onClick={(e) => { e.stopPropagation(); handleDelete(l.id); }}>✕</button>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button style={btnSecondary} onClick={handleAdd}>＋ Add stop</button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, color: '#777', alignSelf: 'center' }}>
          green ●=pose · grey ●=no pose
        </span>
      </div>

      {/* Per-stop edit panel */}
      {selected && (
        <div style={{ borderTop: '1px solid #333', paddingTop: 10 }}>
          <div style={{ color: '#bbb', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
            Editing: {selected.labels?.en || selected.id}
          </div>

          {/* Title (per-language label) */}
          <div style={row}>
            <span style={label}>Title (EN)</span>
            <input
              style={input}
              value={selected.labels?.en || ''}
              onChange={(e) => setSel((l) => {
                l.labels = { ...(l.labels || {}), en: e.target.value };
              })}
              placeholder="English title"
            />
          </div>
          <div style={row}>
            <span style={label}>Title (MR)</span>
            <input
              style={input}
              value={selected.labels?.mr || ''}
              onChange={(e) => setSel((l) => {
                l.labels = { ...(l.labels || {}), mr: e.target.value };
              })}
              placeholder="मराठी शीर्षक"
            />
          </div>
          <div style={row}>
            <span style={label}>Title (HI)</span>
            <input
              style={input}
              value={selected.labels?.hi || ''}
              onChange={(e) => setSel((l) => {
                l.labels = { ...(l.labels || {}), hi: e.target.value };
              })}
              placeholder="हिंदी शीर्षक"
            />
          </div>

          {/* Target POI */}
          <div style={row}>
            <span style={label}>Target POI</span>
            <select
              style={input}
              value={selected.targetPoiId || ''}
              onChange={(e) => setSel((l) => { l.targetPoiId = e.target.value; })}
            >
              <option value="">— pick a POI —</option>
              {pois.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </div>
          {pois.length === 0 && (
            <div style={{ ...row, color: '#888', fontSize: 11 }}>
              No POIs found in navHintsConfig.json. Author POIs first via the
              NavHints editor (also Alt+Shift+E).
            </div>
          )}

          {/* Audio narration */}
          <div style={row}>
            <span style={label}>Audio</span>
            <select
              style={input}
              value={selected.narrationKey || ''}
              onChange={(e) => setSel((l) => { l.narrationKey = e.target.value; })}
            >
              {NARRATION_KEYS.map((o) => (
                <option key={o.value || 'none'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* 360° pose & pan controls */}
          <>
              <div style={{ ...row, marginTop: 4 }}>
                <span style={label}>Pose</span>
                <span style={{ flex: 1, fontSize: 11, color: '#888', fontFamily: 'ui-monospace, monospace' }}>
                  {selected.camera?.sweepId
                    ? `${selected.camera.sweepId.slice(0, 8)}… · yaw ${selected.camera.rotation?.y ?? 0}°${
                        selected.camera.zoom != null ? ` · zoom ${selected.camera.zoom}` : ''
                      }`
                    : '(none — fly to stop, frame the shot, then click below)'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                <button style={btn} onClick={() => handleCapture(selected.id)}>
                  📍 Set as default view
                </button>
                {selected.camera?.sweepId && (
                  <button style={btnSecondary} onClick={() => handleClearCamera(selected.id)}>
                    Clear
                  </button>
                )}
              </div>
              <div style={{ ...row, color: '#888', fontSize: 11, marginTop: -4, marginBottom: 10 }}>
                Pan / orbit the 3D scene to the angle you want, then click
                “Set as default view” to capture it for this stop.
              </div>

              <div style={row}>
                <span style={label}>Auto-pan</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={!!selected._pan}
                    onChange={(e) => setSel((l) => { l._pan = e.target.checked; })}
                  />
                  Cinematic sweep — default → right
                </label>
              </div>
              {selected._pan && (
                <div style={row}>
                  <span style={label}>Pan angle</span>
                  <input
                    style={{ flex: 1, minWidth: 0 }}
                    type="range"
                    min={1}
                    max={90}
                    step={1}
                    value={selected._panAngle ?? 15}
                    onMouseDown={() => {
                      // Capture the current camera yaw so the live preview
                      // can rotate from the captured-default view as base.
                      const base = selected.camera?.rotation;
                      panPreviewBaseRef.current = base
                        ? { x: base.x ?? 0, y: base.y ?? 0 }
                        : null;
                    }}
                    onTouchStart={() => {
                      const base = selected.camera?.rotation;
                      panPreviewBaseRef.current = base
                        ? { x: base.x ?? 0, y: base.y ?? 0 }
                        : null;
                    }}
                    onChange={(e) => {
                      const next = Math.max(1, Math.min(90, Number(e.target.value)));
                      setSel((l) => { l._panAngle = next; });
                      // Live preview: turn the camera RIGHT by `next` so the
                      // author sees the right edge of the pan arc. Matterport
                      // yaw is positive-left, so subtract from baseYaw.
                      const base = panPreviewBaseRef.current;
                      if (base) {
                        try {
                          matterportService.state?.sdk?.Camera?.setRotation?.(
                            { x: base.x, y: base.y - next },
                            { speed: 200 },
                          )?.catch?.(() => {});
                        } catch (_) {}
                      }
                    }}
                    onMouseUp={() => {
                      // Snap back to the captured default view so the next
                      // footer click starts from the same baseline.
                      const base = panPreviewBaseRef.current;
                      if (base) {
                        try {
                          matterportService.state?.sdk?.Camera?.setRotation?.(
                            { x: base.x, y: base.y },
                            { speed: 80 },
                          )?.catch?.(() => {});
                        } catch (_) {}
                      }
                      panPreviewBaseRef.current = null;
                    }}
                    onTouchEnd={() => {
                      const base = panPreviewBaseRef.current;
                      if (base) {
                        try {
                          matterportService.state?.sdk?.Camera?.setRotation?.(
                            { x: base.x, y: base.y },
                            { speed: 80 },
                          )?.catch?.(() => {});
                        } catch (_) {}
                      }
                      panPreviewBaseRef.current = null;
                    }}
                  />
                  <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8, minWidth: 70, textAlign: 'right' }}>
                    {selected._panAngle ?? 15}° right
                  </span>
                </div>
              )}
              {selected._pan && (
                <div style={row}>
                  <span style={label}>Pan speed</span>
                  <input
                    style={{ flex: 1, minWidth: 0 }}
                    type="range"
                    min={0.1}
                    max={5}
                    step={0.1}
                    value={selected._panSpeed ?? 1}
                    onChange={(e) => setSel((l) => {
                      l._panSpeed = Math.max(0.1, Math.min(20, Number(e.target.value)));
                    })}
                  />
                  <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8, minWidth: 70, textAlign: 'right' }}>
                    {(selected._panSpeed ?? 1).toFixed(1)}× {(selected._panSpeed ?? 1) < 1 ? '(slower)' : (selected._panSpeed ?? 1) > 1 ? '(faster)' : ''}
                  </span>
                </div>
              )}
            </>

          {/* ── Display options ───────────────────────────────────────
              Per-stop control over the proximity gallery tile strip and
              the fullscreen image overlay auto-open behaviour.            */}
          <div style={{ borderTop: '1px solid #2a2a32', marginTop: 12, paddingTop: 10 }}>
            <div style={{ color: '#bbb', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>
              🖼️ Display options
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={selected.showGalleryTiles !== false}
                onChange={(e) => setSel((l) => {
                  // Default state is `true`. Only persist when the author
                  // turns it off so JSON stays minimal.
                  if (e.target.checked) delete l.showGalleryTiles;
                  else l.showGalleryTiles = false;
                })}
              />
              Show proximity gallery tiles
              <span style={{ color: '#777', fontSize: 11 }}>
                (right-side strip when near POI)
              </span>
            </label>

            {(() => {
              const hasOverlay = !!selected.imageOverlay?.images?.length;
              if (!hasOverlay) {
                return (
                  <div style={{ fontSize: 11, color: '#777', marginTop: 4 }}>
                    No fullscreen overlay configured for this stop — add
                    images to <code>imageOverlay.images</code> in{' '}
                    <code>quickNavConfig.json</code> first, or run{' '}
                    <code>scripts/sync-poi-galleries.js</code>.
                  </div>
                );
              }
              const ao = selected.imageOverlay.autoOpen || {};
              const setAO = (key, value) => setSel((l) => {
                const overlay = l.imageOverlay || {};
                const next = { ...(overlay.autoOpen || {}) };
                next[key] = value;
                // When BOTH flags are true (the default), drop the autoOpen
                // object so the JSON stays clean.
                if (next.quicknav !== false && next.tour !== false) {
                  if (overlay.autoOpen) {
                    const { autoOpen: _drop, ...rest } = overlay;
                    l.imageOverlay = rest;
                  }
                } else {
                  l.imageOverlay = { ...overlay, autoOpen: next };
                }
              });
              const quicknavOn = ao.quicknav !== false;
              const tourOn = ao.tour !== false;
              const displayMode =
                selected.imageOverlay.displayMode === 'sidePanel'
                  ? 'sidePanel'
                  : 'fullscreen';
              const setDisplayMode = (mode) => setSel((l) => {
                const overlay = l.imageOverlay || {};
                if (mode === 'fullscreen') {
                  // Default — drop the field so JSON stays minimal.
                  const { displayMode: _drop, ...rest } = overlay;
                  l.imageOverlay = rest;
                } else {
                  l.imageOverlay = { ...overlay, displayMode: mode };
                }
              });
              return (
                <>
                  <div style={{ ...row, marginBottom: 6 }}>
                    <span style={label}>Layout</span>
                    <select
                      style={input}
                      value={displayMode}
                      onChange={(e) => setDisplayMode(e.target.value)}
                    >
                      <option value="fullscreen">Fullscreen (covers 3D)</option>
                      <option value="sidePanel">Side panel (¼ window, right)</option>
                    </select>
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
                    Auto-open overlay ({selected.imageOverlay.images.length}{' '}
                    image{selected.imageOverlay.images.length === 1 ? '' : 's'}):
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={quicknavOn}
                      onChange={(e) => setAO('quicknav', e.target.checked)}
                    />
                    On Quick Nav arrival (footer)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={tourOn}
                      onChange={(e) => setAO('tour', e.target.checked)}
                    />
                    On Guided Tour step
                  </label>
                  {!quicknavOn && !tourOn && (
                    <div style={{ fontSize: 11, color: '#c9a13c', marginTop: 4 }}>
                      Overlay will never auto-open. Images remain reachable
                      via the proximity tiles (if enabled) + lightbox.
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Footer actions */}
      <div style={{ display: 'flex', gap: 6, marginTop: 12, borderTop: '1px solid #333', paddingTop: 10 }}>
        <button style={btn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : '💾 Save to project'}
        </button>
        <button style={btnSecondary} onClick={handleCopyJson}>📋 Copy JSON</button>
        <span style={{ flex: 1 }} />
        <button style={btnDanger} onClick={() => {
          if (!window.confirm('Discard all unsaved changes?')) return;
          const c = clone(quickNavConfigSrc);
          c.locations = (c.locations || []).map(normaliseLocation);
          setWorking(c);
          showToast('Reverted to last saved');
        }}>↺ Revert</button>
      </div>

      {toast && (
        <div style={{ marginTop: 10, padding: 6, background: '#1e4a8a', borderRadius: 4, fontSize: 12 }}>
          {toast}
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: '#888' }}>
        Edits apply live to nav. Click Save to persist to{' '}
        <code>src/config/quickNavConfig.json</code> (dev server only).
      </div>
    </div>
  );
};

export default QuickNavEditor;
